/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import i18n from 'i18next';
import pull from 'lodash/pull';

import {
  CategoryNavigator,
  getSelectOptions,
  history,
  useDebounce,
  useGlobalQueries,
} from '@/exceed';

import { urlSearch } from '@/search';

import getOrganizationVideoPermissionsForUser from '../helpers/getOrganizationVideoPermissionsForUser';
import getSelectedVideoStatus from '../helpers/getSelectedVideoStatus';
import getVideosAdminFilters from '../helpers/getVideosAdminFilters';
import getVideosAdminFilterValues from '../helpers/getVideosAdminFilterValues';

import useOrganizationVideoPermissions from '../hooks/useOrganizationVideoPermissions';

import styles from '../styles/videosAdminSearchContainer.module.scss';

const VideosAdminSearchContainer = ({ isOnDemandHomePage = false, setIsShowingLoadingWheel }) => {
  const { pathname, search } = useLocation();

  const hasActiveFilters = () => {
    const searchParams = urlSearch.getParams(search);
    searchParams.delete('search');
    searchParams.delete('status');
    return Boolean(searchParams.toString()?.length);
  };

  const params = useParams();
  const { orgId, type } = params;
  const searchFilters = getVideosAdminFilters({ params });
  const [filtersShouldShow, setFiltersShouldShow] = useState(hasActiveFilters());
  const [searchValue, setSearchValue] = useState(urlSearch.getValue('search') || '');
  const [selectedFilterValues, setSelectedFilterValues] = useState(
    getVideosAdminFilterValues(searchFilters),
  );
  const [isUserEditingFilters, setIsUserEditingFilters] = useState(false);

  useEffect(() => {
    if (!isUserEditingFilters) {
      setFiltersShouldShow(hasActiveFilters());
      setSearchValue(urlSearch.getValue('search') || '');
      setSelectedFilterValues(getVideosAdminFilterValues(searchFilters));
    } else {
      setIsUserEditingFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleUpdateSearchURL = useDebounce((updatedSearchValue) => {
    if (updatedSearchValue === '') {
      urlSearch.deleteValue('search');
    } else {
      urlSearch.setValue('search', updatedSearchValue);
    }
  }, 200);

  const handleDraftStateFilterClick = ({ isSelected, value }) => {
    const { draft_state } = selectedFilterValues;

    // Create a clonedDraftState as a shallow copy of draft_state or an empty array if draft_state is not defined
    const clonedDraftState = draft_state ? [...draft_state] : [];

    // Optimistically update the selectedFilterValues with the updated draft_state value
    setSelectedFilterValues({
      ...selectedFilterValues,
      draft_state: isSelected
        ? [...(draft_state ?? []), value]
        : pull(clonedDraftState ?? [], value),
    });

    let updateValue;

    // Update the updateValue based on the draft_state length and isSelected flag. This should convert the array to comma-separated values
    if (draft_state?.length > 0)
      updateValue = isSelected
        ? [clonedDraftState, value].join(',')
        : pull(clonedDraftState, value).join(',');
    if (!draft_state || draft_state?.length === 0) updateValue = isSelected ? value : null;

    if (!isSelected && draft_state?.length === 1) {
      urlSearch.deleteValue('draft_state');
    } else {
      urlSearch.setValue('draft_state', updateValue);
    }
  };

  const onSelectChange = ({ id }) => {
    if (Number(orgId) === id) return;

    const rootUrl = `/videos/organizations/${id}/${type}`;
    const url = isOnDemandHomePage || type === 'live' ? `${rootUrl}${search}` : rootUrl;

    history.push(url);
  };

  const onSearchInputChange = (updatedSearchValue) => {
    handleUpdateSearchURL(updatedSearchValue);

    setSearchValue(updatedSearchValue);
    setIsShowingLoadingWheel(true);
  };

  const onSearchInputFocus = () => {
    if (hasActiveFilters()) return;

    setFiltersShouldShow(true);
  };

  const onCategoryNavigatorBlur = () => {
    if (hasActiveFilters() || searchValue) return;

    setFiltersShouldShow(false);
  };

  const onClearSearchClick = () => onSearchInputChange('');

  const onFilterClick = ({ key, value, isSingleSelect, isToggle, isSelected }) => {
    setIsUserEditingFilters(true);

    if (key === 'draft_state') return handleDraftStateFilterClick({ isSelected, value });

    if (isSingleSelect) {
      urlSearch.setValue(key, isToggle && value === false ? true : value);
    } else {
      urlSearch.appendValue(key, value);
    }

    setSelectedFilterValues(getVideosAdminFilterValues(searchFilters));
  };

  const onClearFiltersClick = () => {
    const urlSearchValue = urlSearch.getValue('search');

    if (!urlSearchValue) {
      setFiltersShouldShow(false);
    }

    setSelectedFilterValues({});

    let url = urlSearchValue ? `${pathname}?search=${urlSearchValue}` : pathname;

    if (type === 'live') {
      url += `${urlSearchValue ? '&' : '?'}status=${getSelectedVideoStatus()}`;
    }

    return history.push(url);
  };

  const shouldShowFilters = () => hasActiveFilters() || Boolean(searchValue) || filtersShouldShow;

  const globalQueries = useGlobalQueries();
  const { organizations } = globalQueries;
  const shouldShowOrganizationsSelect = () => organizations.data.organizations?.length > 1;

  const searchPlaceholder = i18n.t('search-videos');

  const selectOptions = getSelectOptions(
    getOrganizationVideoPermissionsForUser({
      ...globalQueries,
      organizationPermissions: useOrganizationVideoPermissions(),
    }),
  );

  const selectPlaceholder = i18n.t('select-an-organization');
  const shouldShowSelect = shouldShowOrganizationsSelect();
  const selectValue = selectOptions.find((selectOption) => selectOption.id === Number(orgId));

  return (
    <CategoryNavigator
      className={styles.element}
      selectOptions={selectOptions}
      shouldShowSelect={shouldShowSelect}
      selectPlaceholder={selectPlaceholder}
      onSelectChange={onSelectChange}
      selectValue={selectValue}
      searchPlaceholder={searchPlaceholder}
      searchValue={searchValue}
      onClearSearchClick={onClearSearchClick}
      onSearchInputChange={onSearchInputChange}
      onSearchInputFocus={onSearchInputFocus}
      onCategoryNavigatorBlur={onCategoryNavigatorBlur}
      shouldShowFilters={shouldShowFilters()}
      onFilterClick={onFilterClick}
      onClearFiltersClick={onClearFiltersClick}
      shouldAnimateFilters
      filters={searchFilters}
      selectedFilterValues={selectedFilterValues}
      shouldShowClearFilters={hasActiveFilters()}
    />
  );
};

VideosAdminSearchContainer.propTypes = {
  isOnDemandHomePage: PropTypes.bool,
  setIsShowingLoadingWheel: PropTypes.func.isRequired,
};

export default VideosAdminSearchContainer;
